import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { getEmployees } from "../services/employeeService";
import { normalizeRole, USER_ROLES } from "../auth/roles";

const UserRoleContext = createContext({
  loading: true,
  role: USER_ROLES.MEMBER,
  employee: null,
  error: "",
  refreshUserRole: async () => {},
});

export function UserRoleProvider({ children }) {
  const { accounts, inProgress } = useMsal();

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(USER_ROLES.MEMBER);
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState("");

  const accountEmail = accounts?.[0]?.username || "";

  const refreshUserRole = useCallback(async () => {
    // MSAL hasn't finished determining the signed-in account yet (e.g. right
    // after a page reload); wait instead of prematurely defaulting to MEMBER,
    // which would fail canAccessPage checks for non-default pages.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (!accountEmail) {
      setLoading(false);
      setRole(USER_ROLES.MEMBER);
      setEmployee(null);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const employees = await getEmployees(accountEmail);
      const matchedEmployee = employees.find(
        (item) => item.email?.toLowerCase() === accountEmail.toLowerCase()
      );

      if (!matchedEmployee) {
        setRole(USER_ROLES.MEMBER);
        setEmployee(null);
        localStorage.removeItem("employee_id");
      } else {
        const resolvedRole = normalizeRole(matchedEmployee.role_name);
        setRole(resolvedRole);
        setEmployee(matchedEmployee);
        localStorage.setItem("employee_id", String(matchedEmployee.employee_id));
      }
    } catch (requestError) {
      setError("Unable to fetch role information");
      setRole(USER_ROLES.MEMBER);
      setEmployee(null);
    } finally {
      setLoading(false);
    }
  }, [accountEmail, inProgress]);

  useEffect(() => {
    refreshUserRole();
  }, [refreshUserRole]);

  const value = useMemo(
    () => ({
      loading,
      role,
      employee,
      error,
      refreshUserRole,
    }),
    [loading, role, employee, error, refreshUserRole]
  );

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
}

export function useUserRole() {
  return useContext(UserRoleContext);
}
